// Streak & trend signal engine — extracted verbatim from StatsView (2026-07)
// so the Season Dashboard, player detail sheets, and the Smart Scout payload
// share one implementation. Distinct from src/lib/streaks.ts (the home-screen
// hot/cold summary) — do not merge.
//
// Pure + framework-free. `sortedGames` must be completed games in ascending
// date order; `stats` is the buildSeasonStats rollup map.

export type StreakSignal = {
  playerId: string;
  playerName: string;
  playerNumber: string;
  direction: 'hot' | 'cold';
  label: string;
  detail: string;
  magnitude: number;
};

export type PlayerSignalGroup = {
  playerId: string;
  playerName: string;
  playerNumber: string;
  signals: StreakSignal[];
};

export const shortName = (name: string) => {
  const parts = (name || '').trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0][0]}. ${parts[parts.length - 1]}` : parts[0];
};

export function computeStreakSignals(
  players: any[],
  sortedGames: any[],
  stats: Record<string, any>,
): StreakSignal[] {
  if (sortedGames.length < 2) return [];
  const signals: StreakSignal[] = [];

  const nr = (val: any): number | null => {
    if (val == null || val === '') return null;
    const n = parseFloat(val);
    if (isNaN(n)) return null;
    return n > 1 ? n / 100 : n;
  };

  // Team avg S% for command streak comparison
  let teamSPctSum = 0, teamSPctCount = 0;
  Object.values(stats).forEach((s: any) => {
    if (s.pitching.sPct != null) { teamSPctSum += s.pitching.sPct; teamSPctCount++; }
  });
  const teamAvgSPct = teamSPctCount > 0 ? teamSPctSum / teamSPctCount : null;

  // Walk backward from most recent game counting consecutive matching games
  const streak = (arr: any[], ok: (s: any) => boolean): number => {
    let n = 0;
    for (let i = arr.length - 1; i >= 0; i--) { if (ok(arr[i])) n++; else break; }
    return n;
  };

  const push = (playerId: string, playerName: string, playerNumber: string, direction: 'hot' | 'cold', label: string, detail: string, magnitude: number) =>
    signals.push({ playerId, playerName, playerNumber, direction, label, detail, magnitude });

  players.forEach(player => {
    const sn = shortName(player.name);
    const pid = player.id;
    const pn = player.number;

    const batting = sortedGames
      .map(g => g.stats?.[pid])
      .filter((s): s is any => s != null && (s.pa > 0 || s.ab > 0));

    const pitching = sortedGames
      .map(g => g.stats?.[pid])
      .filter((s): s is any => s != null && ((s.ip || 0) > 0 || (s.pitches || 0) > 0));

    // ── Batting streaks ────────────────────────────────────────────────
    if (batting.length >= 2) {
      const hitStr = streak(batting, s => (s.h || 0) >= 1);
      const obStr  = streak(batting, s => ((s.h || 0) + (s.bb || 0)) >= 1);
      const rbiStr = streak(batting, s => (s.rbi || 0) >= 1);

      if (hitStr >= 2) push(pid, sn, pn, 'hot', `${hitStr}-game hit streak`, '', hitStr * 10);
      // On-base streak only shown when it extends beyond the hit streak (walks carrying it)
      if (obStr >= 2 && obStr > hitStr) push(pid, sn, pn, 'hot', `${obStr}-game on-base streak`, '', obStr * 9);
      if (rbiStr >= 2) push(pid, sn, pn, 'hot', `${rbiStr}-game RBI streak`, '', rbiStr * 8);

      // Cold bat: hitless in last N games with ≥ 2 AB each
      let coldN = 0, coldAB = 0;
      for (let i = batting.length - 1; i >= 0; i--) {
        const s = batting[i];
        if ((s.h || 0) === 0 && (s.ab || 0) >= 2) { coldN++; coldAB += (s.ab || 0); } else break;
      }
      if (coldN >= 2) push(pid, sn, pn, 'cold', `0-for-${coldAB} last ${coldN} games`, '', coldN * 8);
    }

    // ── Batting rolling trends (last 3 games vs season) ────────────────
    if (batting.length >= 3) {
      const pStats = stats[pid]?.batting;
      const last3 = batting.slice(-3);

      if (pStats?.ops != null && pStats.ops > 0) {
        const w = last3.reduce((a: number, s: any) => a + (s.pa || 1), 0);
        const l3ops = last3.reduce((a: number, s: any) => a + (s.ops || 0) * (s.pa || 1), 0) / w;
        const delta = l3ops - pStats.ops;
        if (Math.abs(delta / pStats.ops) >= 0.15) {
          const hot = delta > 0;
          push(pid, sn, pn, hot ? 'hot' : 'cold',
            `OPS ${hot ? '↑' : '↓'} last 3 games`,
            `${l3ops.toFixed(3).replace(/^0/, '')} vs ${pStats.ops.toFixed(3).replace(/^0/, '')} season`,
            Math.abs(delta / pStats.ops) * 100);
        }
      }

      if (pStats?.ab > 0 && pStats?.so != null) {
        const seasonK = pStats.so / pStats.ab;
        const l3ab = last3.reduce((a: number, s: any) => a + (s.ab || 0), 0);
        const l3so = last3.reduce((a: number, s: any) => a + (s.so || 0), 0);
        if (l3ab > 0 && seasonK > 0) {
          const l3k = l3so / l3ab;
          const delta = l3k - seasonK;
          if (Math.abs(delta / seasonK) >= 0.15 && (delta < 0 || l3k >= 0.34)) {
            const hot = delta < 0;
            push(pid, sn, pn, hot ? 'hot' : 'cold',
              `K rate ${hot ? '↓' : '↑'} last 3 games`,
              `${Math.round(l3k * 100)}% vs ${Math.round(seasonK * 100)}% season`,
              Math.abs(delta / seasonK) * 80);
          }
        }
      }
    }

    // ── Pitching streaks ───────────────────────────────────────────────
    if (pitching.length >= 2) {
      const scoreless = streak(pitching, s => s.era === 0 && (s.ip || 0) > 0);
      if (scoreless >= 2) push(pid, sn, pn, 'hot', `${scoreless} scoreless outings`, '', scoreless * 12);

      if (teamAvgSPct != null) {
        const cmdStr = streak(pitching, s => { const sp = nr(s.sPct); return sp != null && sp >= teamAvgSPct; });
        if (cmdStr >= 2) push(pid, sn, pn, 'hot', `Locating Pitches: ${cmdStr} outings ${Math.round(teamAvgSPct * 100)}%+ strikes`, '', cmdStr * 9);
      }
    }

    // ── Pitching rolling trends (last 3 outings vs season) ────────────
    if (pitching.length >= 3) {
      const pStats = stats[pid]?.pitching;
      const last3 = pitching.slice(-3);

      if (pStats?.whip != null && pStats.whip > 0) {
        const l3ip = last3.reduce((a: number, s: any) => a + (s.ip || 0), 0);
        if (l3ip > 0) {
          const l3whip = last3.reduce((a: number, s: any) => a + (s.whip || 0) * (s.ip || 0), 0) / l3ip;
          const delta = l3whip - pStats.whip;
          if (Math.abs(delta / pStats.whip) >= 0.15) {
            const hot = delta < 0;
            push(pid, sn, pn, hot ? 'hot' : 'cold',
              `WHIP ${hot ? '↓' : '↑'} last 3 outings`,
              `${l3whip.toFixed(2)} vs ${pStats.whip.toFixed(2)} season`,
              Math.abs(delta / pStats.whip) * 90);
          }
        }
      }

      if (pStats?.sPct != null && pStats.sPct > 0) {
        const l3pitches = last3.reduce((a: number, s: any) => a + (parseInt(s.pitches) || 0), 0);
        if (l3pitches > 0) {
          const l3sp = last3.reduce((a: number, s: any) => {
            const sp = nr(s.sPct);
            return sp != null ? a + sp * (parseInt(s.pitches) || 1) : a;
          }, 0) / l3pitches;
          const delta = l3sp - pStats.sPct;
          if (Math.abs(delta / pStats.sPct) >= 0.15) {
            const hot = delta > 0;
            push(pid, sn, pn, hot ? 'hot' : 'cold',
              `S% ${hot ? '↑' : '↓'} last 3 outings`,
              `${Math.round(l3sp * 100)}% vs ${Math.round(pStats.sPct * 100)}% season`,
              Math.abs(delta / pStats.sPct) * 80);
          }
        }
      }
    }
  });

  return signals.sort((a, b) => b.magnitude - a.magnitude);
}

/** Group signals by player, hot-first, sorted by top magnitude — verbatim StatsView behavior. */
export function groupSignalsByPlayer(streakSignals: StreakSignal[]): PlayerSignalGroup[] {
  return Object.values(
    streakSignals.reduce((acc: Record<string, any>, sig) => {
      if (!acc[sig.playerId]) {
        acc[sig.playerId] = { playerId: sig.playerId, playerName: sig.playerName, playerNumber: sig.playerNumber, signals: [] };
      }
      acc[sig.playerId].signals.push(sig);
      return acc;
    }, {})
  ).sort((a: any, b: any) => {
    const aHot = a.signals.some((s: any) => s.direction === 'hot') ? 0 : 1;
    const bHot = b.signals.some((s: any) => s.direction === 'hot') ? 0 : 1;
    if (aHot !== bHot) return aHot - bHot;
    return b.signals[0].magnitude - a.signals[0].magnitude;
  }) as PlayerSignalGroup[];
}
